import React, { useState } from 'react';
import _ from 'lodash';
import { displayNumber } from '../../../helpers';
import Alert from '../../elements/Alert';

type Props = {
  showTaskId: boolean,
  monthReport: {
    stats: {
      users: Array<any>,
      categories: any,
      logs: Array<any>,
      project_total_hours: number,
      category_colors: any,
    }
  },
};

const BreakdownTable = (props: Props) => {
  const { monthReport, showTaskId } = props;
  const userStats = _.sortBy(monthReport.stats.users, 'surname');
  const categoryStats = monthReport.stats.categories;
  const categoriesSorted = _.sortBy(_.keys(categoryStats));
  const categoryStatsTotal = _.sumBy(_.map(categoryStats));
  const logsByUser = _.groupBy(monthReport.stats.logs, 'user_id');
  const usersIds = Object.keys(logsByUser);
  const logsByUserByDay: any = {};

  usersIds.forEach(userId => {
    const logs = _.groupBy(logsByUser[userId], 'date');
    logsByUserByDay[userId] = Object.entries(logs).map((e) => ({ [e[0]]: e[1] }));
  });

  const [searchTerm, setSearchTerm] = useState('');

  const handleSearchChange = (e: any) => {
    setSearchTerm(e.target.value);
  };

  const filterLogs = (logs: any[]) => {
    if (!searchTerm) return logs;

    return logs.filter((log) => log.task_id && log.task_id.toLowerCase().includes(searchTerm.toLowerCase()));
  };

  if (monthReport.stats.project_total_hours === 0) {
    return (
      <div className="col-12">
        <Alert display message="No statistics available." />
      </div>
    );
  }

  const getUserName = (userId: number) => {
    const user = _.find(monthReport.stats.users, ({ id: _.toInteger(userId) }));
    if (!user) return '';

    return `${user.name} ${user.surname}`;
  };

  const getCategoryIconAndName = (category: string) => {
    if (category && monthReport.stats.category_colors[category]) {
      const color = monthReport.stats.category_colors[category];

      return (
        <span>
          <i className="fa fa-circle mr-2" style={{ color }} aria-hidden="true" />
          {_.startCase(category)}
        </span>
      );
    }

    return null;
  };

  const getUserLogs = (userId: number) => {
    if (!logsByUserByDay[userId]) return [];

    return logsByUserByDay[userId];
  };

  const renderDateCell = (log: any, isFirstNormalLog: boolean) => {
    if (log.is_incident_segment) {
      return (
        <>
          <div>{log.date}</div>
          {log.checkin_time && (
            <div className="text-muted small">{log.checkin_time}</div>
          )}
          <div className="incident-period-range">{log.period_time_range}</div>
        </>
      );
    }

    if (isFirstNormalLog) {
      return log.checkin_time ? (
        <>
          <div>{log.date}</div>
          <div className="text-muted small">{log.checkin_time}</div>
        </>
      ) : (
        log.date
      );
    }

    return '';
  };

  return (
    <>
      <div className="col-md-6">
        <table className="table mt-2">
          <thead className="thead-light">
            <tr className="table-project-header">
              <th>Developers on project</th>
              <th className="text-center">Total hours</th>
            </tr>
          </thead>
          <tbody>
            {userStats.map((user: any) => (
              <tr key={user.id}>
                <td>
                  {`${user.name} ${user.surname}`}
                </td>
                <td className="text-right">
                  <span className="mr-3">
                    {`${displayNumber(user.combined_total_hours ?? user.total_hours)}h`}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="col-md-6">
        <table className="table mt-2">
          <thead className="thead-light">
            <tr className="table-project-header">
              <th>
                <span className="device-big">Service </span>
                Category
              </th>
              <th>Percentage</th>
              <th className="text-center">
                <span className="device-big">Total </span>
                Hours
              </th>
            </tr>
          </thead>
          <tbody>
            {categoriesSorted.map((category) => (
              <tr key={category}>
                <td>
                  {getCategoryIconAndName(category)}
                </td>
                <td>
                  {Math.round((categoryStats[category] / categoryStatsTotal) * 100) + '%'}
                </td>
                <td className="text-right">
                  <span className="mr-3">
                    {`${displayNumber(categoryStats[category])}h`}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showTaskId && (
        <div className="search-container">
          <input
            type="text"
            placeholder="Search by Task ID"
            value={searchTerm}
            onChange={handleSearchChange}
            className="form-control mb-3"
          />
        </div>
      )}

      {userStats.map(user => {
        const allLogs = getUserLogs(user.id);
        const userStat = _.find(userStats, ['id', user.id]);

        return (
          <div className="col-12" key={user.id}>
            <table className="table mt-2 user-breakdown-table">
              <thead className="thead-light">
                <tr>
                  <th className="text-center">{getUserName(user.id)}</th>
                </tr>
                <tr className="table-project-header">
                  <th className="date-heading">Date (work-hours)</th>
                  <th className="desc-heading">Description of service</th>
                  {showTaskId && <th className="device-big service-heading">Task ID</th>}
                  <th className="device-big service-heading">Service category</th>
                  <th className="text-center efforts-heading">
                    <span className="device-big">Efforts in </span>
                    h
                  </th>
                </tr>
              </thead>
              <tbody>
                {allLogs.map((date: any) => {
                  const logsForDate: any[] = date[Object.keys(date)[0]];
                  let firstNormalLogSeen = false;

                  return logsForDate.map((log: any, ind: number) => {
                    const filteredLogs = filterLogs([log]);
                    const isIncident = log.is_incident_segment;
                    const isFirstNormalLog = !isIncident && !firstNormalLogSeen;
                    if (!isIncident) firstNormalLogSeen = true;

                    return filteredLogs.length > 0 ? (
                      <tr
                        key={`${log.date}-${ind}-${Math.random()}`}
                        className={isIncident ? 'incident-log-row' : ''}
                      >
                        <td
                          className={
                            !isIncident && !isFirstNormalLog ? 'user-breakdown-date-continuation' : undefined
                          }
                        >
                          {renderDateCell(log, isFirstNormalLog)}
                        </td>
                      <td className="log-cell-description">
                        {isIncident && <span className="incident-row-icon" title="Incident">&#9888; </span>}
                        {log.description}
                      </td>
                      {showTaskId && <td className="device-big">{log.task_id}</td>}
                      <td className="device-big">{getCategoryIconAndName(log.category)}</td>
                      <td className="text-right">
                        <div>{displayNumber(log.time_spent)}</div>
                        {isIncident && log.actual_hours != null && log.multiplier != null && (
                          <div className="incident-time-breakdown">
                            {`(${displayNumber(log.actual_hours)}h × ${log.multiplier}x)`}
                          </div>
                        )}
                      </td>
                    </tr>
                    ) : null;
                  });
                })}
              </tbody>
            </table>
            <div className="total-hours-container">
              <span>Total hours</span>
              <div className="text-right table-secondary small-screen-total">
                {displayNumber(userStat?.combined_total_hours ?? userStat?.total_hours)}
              </div>
              <div className="text-right table-secondary device-big d-md-none">
                {displayNumber(userStat?.combined_total_hours ?? userStat?.total_hours)}
              </div>
            </div>
          </div>
        );
      })}
    </>
  );
};

export default BreakdownTable;
